import {ReactNode, useMemo, useRef} from 'react';
import useDialogState from 'hooks/useDialogState';
import clsx from 'clsx';
import {ChoiceProps} from '@/hooks/types';
import Spinner from './Spinner';
import {Input} from '@/shadcn_components/ui/input';
import {XMarkIcon} from '@heroicons/react/24/outline';
import {Popover, PopoverContentWithoutPortal, PopoverTrigger} from '@/shadcn_components/ui/popover';
import {Button} from '@/shadcn_components/ui/button';
import {ChevronsUpDownIcon, SearchIcon} from 'lucide-react';
import {BaseRecord} from '@refinedev/core';

export type AutocompleteProps = {
  choices: ChoiceProps[]; // choice to select from
  optionValue?: string; // field name of the value (usually id)
  getOptionValue?: (choice: ChoiceProps) => any; //custom get value
  optionText?: string; //field of the text representation (usually name, label, etc.)
  getOptionText?: (choice: ChoiceProps) => string; //custom get text representation for choice
  renderOption?: (choice: ChoiceProps) => ReactNode; //render option/choice
  isLoading?: boolean; //indicate loading
  isMore?: boolean; //indicate that there are more results, you should type to search
  noOptionText?: ReactNode; // custom no Option text
  moreText?: ReactNode; //custom more text
  dropdownClassName?: string;
  // debounceWait?: number;
  value?: ChoiceProps;
  onSelect?: (value?: ChoiceProps) => void;
  clearSearchOnSelect?: boolean; //when click (select) an item, clear the search input
  onInputChange?: (text: string) => void;
  EmptyValue?: boolean | ChoiceProps;
  placeholder_input?: string;
} & React.ComponentProps<'div'>;

const Autocomplete2 = (props: AutocompleteProps) => {
  const {
    placeholder = 'Lựa chọn',
    placeholder_input = 'Tìm kiếm',
    optionText = 'label',
    optionValue = 'id',
    renderOption = choice => <p className="text-sm">{getOptionText(choice)}</p>,
    isLoading = false,
    isMore = false,
    getOptionValue = choice => choice?.[optionValue] || String(choice.id),
    getOptionText = choice => choice?.[optionText] || String(choice.id),
    choices,
    className,
    dropdownClassName,
    value,
    onSelect,
    onInputChange,
    clearSearchOnSelect,
    noOptionText = isLoading ? (
      <Spinner className="w-4 h-4" />
    ) : (
      <p className="p-2 rounded-md">Không tìm thấy</p>
    ),
    moreText = <p className="p-2 rounded-md"> ... ... </p>,
    EmptyValue = false,
    ...rest
  } = props;

  const _EmptyValue: ChoiceProps | undefined = useMemo(() => {
    if (!EmptyValue) return;
    if (EmptyValue === true) return {id: 0, [optionText]: '--- Không chọn ---'};
    return EmptyValue;
  }, [EmptyValue]);

  const menuState = useDialogState();

  const inputRef = useRef<any>();

  function chooseItem(v: ChoiceProps) {
    if (inputRef.current) {
      if (clearSearchOnSelect) inputRef.current.value = '';
      else inputRef.current.value = getOptionText(v);
      onSelect?.(getOptionValue(v));
    }
    menuState.closeDialog();
  }

  // function clearChoice() {
  //   if (inputRef.current) {
  //     inputRef.current.value = '';
  //     onSelect?.(getOptionValue(_EmptyValue));
  //   }
  //   menuState.closeDialog();
  // }

  return (
    // <div className={clsx('relative', className)} {...rest}>
    <Popover open={menuState.open} onOpenChange={menuState.setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          // role="combobox"
          // aria-expanded={menuState.open}
          // className="w-[200px] justify-between"
          className='font-normal'
          >
          {value ? getOptionText(value) : placeholder}
          <ChevronsUpDownIcon className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContentWithoutPortal className="pointer-events-auto">
        <div className="w-full relative flex flex-row items-center p-1 border-b">
          {isLoading ? (
            <Spinner size={'sm'} />
          ) : (
            <SearchIcon className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          )}
          <Input
            placeholder={placeholder_input}
            onChange={e => {
              onInputChange?.(e.target.value);
            }}
            onFocus={e => {
              menuState.openDialog();
            }}
            ref={inputRef}
          />

          <XMarkIcon
            className="w-5 h-5 absolute right-2 hover:bg-secondary"
            role="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              inputRef.current.value = '';
              onSelect?.(undefined);
            }}
          />
        </div>

        <div
          // ref={dropdownRef}
          className={clsx(
            'flex flex-col w-full h-[300px] overflow-y-auto gap-1 scroll',
            // 'absolute top-full left-0 z-50 flex flex-col border border-gray-200 rounded-md bg-white w-full max-h-96 overflow-y-auto gap-1',
            // 'absolute top-full left-0 z-50 max-h-96 bg-white overflow-y-auto',
            dropdownClassName,
          )}>
          {_EmptyValue && (
            <div
              className="hover:bg-secondary cursor-pointer p-2 rounded-md"
              onClick={() => chooseItem(_EmptyValue)}>
              {renderOption(_EmptyValue)}
            </div>
          )}
          {choices && choices.length > 0
            ? choices.map((v, i) => (
                <div
                  className="hover:bg-secondary cursor-pointer p-2 rounded-md"
                  key={i}
                  onClick={() => chooseItem(v)}>
                  {renderOption(v)}
                </div>
              ))
            : noOptionText}
          {isMore && moreText}
        </div>
      </PopoverContentWithoutPortal>
    </Popover>
    // </div>
  );
};

export default Autocomplete2;
